/**
 * Core timer utilities for clock-quantized pomodoro periods
 *
 * Activity periods: :00-:25 and :30-:55 (25 minutes each)
 * Rest periods: :25-:30 and :55-:00 (5 minutes each)
 */

export interface Period {
  start: Date;
  end: Date;
  type: 'activity' | 'rest';
}

/**
 * Calculate the next period boundary (:00, :25, :30, or :55)
 */
export function getNextBoundary(now: Date = new Date()): Date {
  const minutes = now.getMinutes();
  const nextBoundary = new Date(now);
  nextBoundary.setSeconds(0, 0);

  if (minutes < 25) {
    nextBoundary.setMinutes(25);
  } else if (minutes < 30) {
    nextBoundary.setMinutes(30);
  } else if (minutes < 55) {
    nextBoundary.setMinutes(55);
  } else {
    nextBoundary.setHours(nextBoundary.getHours() + 1);
    nextBoundary.setMinutes(0);
  }

  return nextBoundary;
}

/**
 * Calculate the current period boundaries
 */
export function getCurrentPeriod(now: Date = new Date()): Period {
  const minutes = now.getMinutes();
  const periodStart = new Date(now);
  const periodEnd = new Date(now);
  periodStart.setSeconds(0, 0);
  periodEnd.setSeconds(0, 0);

  if (minutes < 25) {
    // Activity period :00-:25
    periodStart.setMinutes(0);
    periodEnd.setMinutes(25);
    return { start: periodStart, end: periodEnd, type: 'activity' };
  } else if (minutes < 30) {
    // Rest period :25-:30
    periodStart.setMinutes(25);
    periodEnd.setMinutes(30);
    return { start: periodStart, end: periodEnd, type: 'rest' };
  } else if (minutes < 55) {
    // Activity period :30-:55
    periodStart.setMinutes(30);
    periodEnd.setMinutes(55);
    return { start: periodStart, end: periodEnd, type: 'activity' };
  } else {
    // Rest period :55-:00
    periodStart.setMinutes(55);
    periodEnd.setHours(periodEnd.getHours() + 1);
    periodEnd.setMinutes(0);
    return { start: periodStart, end: periodEnd, type: 'rest' };
  }
}

/**
 * Get the next FULL ACTIVITY period (not current partial, never rest)
 * Used for planning the next pomodoro
 * ALWAYS returns an activity period, never a rest period
 */
export function getNextFullPeriod(now: Date = new Date()): Period {
  const minutes = now.getMinutes();
  const currentPeriod = getCurrentPeriod(now);
  const periodStart = new Date(now);
  const periodEnd = new Date(now);
  periodStart.setSeconds(0, 0);
  periodEnd.setSeconds(0, 0);

  // If we're in an activity period, return the NEXT activity period (skip rest)
  if (currentPeriod.type === 'activity') {
    if (minutes < 25) {
      // Currently :00-:25, next activity is :30-:55
      periodStart.setMinutes(30);
      periodEnd.setMinutes(55);
    } else {
      // Currently :30-:55, next activity is next hour :00-:25
      periodStart.setHours(periodStart.getHours() + 1);
      periodStart.setMinutes(0);
      periodEnd.setHours(periodEnd.getHours() + 1);
      periodEnd.setMinutes(25);
    }
  } else {
    // If we're in a rest period, return the NEXT activity period
    if (minutes < 30) {
      // Currently :25-:30 rest, next activity is :30-:55
      periodStart.setMinutes(30);
      periodEnd.setMinutes(55);
    } else {
      // Currently :55-:00 rest, next activity is next hour :00-:25
      periodStart.setHours(periodStart.getHours() + 1);
      periodStart.setMinutes(0);
      periodEnd.setHours(periodEnd.getHours() + 1);
      periodEnd.setMinutes(25);
    }
  }

  return { start: periodStart, end: periodEnd, type: 'activity' };
}

/**
 * Calculate elapsed pomodoros (25-minute periods) since last check-in
 */
export function calculateElapsedPeriods(lastCheckIn: number, now: number = Date.now()): number {
  const diffMs = now - lastCheckIn;
  const diffMinutes = diffMs / (1000 * 60);

  // Each pomodoro is 25 minutes
  return Math.floor(diffMinutes / 25);
}

/**
 * Calculate time remaining until next boundary
 */
export function getTimeUntilBoundary(now: Date = new Date()): number {
  const nextBoundary = getNextBoundary(now);
  return nextBoundary.getTime() - now.getTime();
}

/**
 * Format time duration as "X hours Y minutes" or "Y minutes"
 */
export function formatDuration(ms: number): string {
  const totalMinutes = Math.floor(ms / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
}

/**
 * Format time as HH:MM
 */
export function formatTime(date: Date): string {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Format period as "HH:MM - HH:MM"
 */
export function formatPeriodRange(period: Period): string {
  return `${formatTime(period.start)} - ${formatTime(period.end)}`;
}

/**
 * Format countdown timer as MM:SS
 */
export function formatCountdown(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Generate unique period identifier
 */
export function getPeriodId(periodStart: Date): string {
  return `period-${periodStart.getTime()}`;
}
